import React, { useCallback, useMemo } from "react";
import { ServerInferRequest } from "@ts-rest/core";
import { financialDocumentContract } from "@common/contracts/financial-document.contract";
import { FinancialDocumentBulkAction } from "@common/types/financialDocument";
import { useScope } from "@state/scopeContext.lib";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import {
    BulkActionErrorCode,
    RemovingDocumentErrorCode,
} from "@common/constants/financial-documents";
import { useFinancialDocumentBulkActionMutation } from "@api/financialDocument.api";
import { useFinancialDocumentBulkSelection } from "../FinancialDocumentBulkSelection/useFinancialDocumentBulkSelection";
import { ChildrenProps } from "../../../types";
import { InvalidDocumentCategoryErrorToast } from "./InvalidDocumentCategoryErrorToast";
import { LinkedToImportedAccountErrorToast } from "./LinkedToImportedAccountErrorToast";
import { financialDocumentBulkActionsContext } from "./FinancialDocumentBulkActions";

type FinancialDocumentBulkActionBody = ServerInferRequest<
    typeof financialDocumentContract.bulkAction
>["body"];

export const FinancialDocumentBulkActionsProvider: React.FC<ChildrenProps> = ({
    children,
}) => {
    const { workspaceId } = useScope();
    const toaster = useToaster();
    const bulkSelection = useFinancialDocumentBulkSelection();

    const buildPayload = useCallback(
        (
            action: FinancialDocumentBulkAction,
        ): FinancialDocumentBulkActionBody =>
            bulkSelection.filtersSelection
                ? {
                      workspaceId: workspaceId ?? "",
                      action,
                      filters: {
                          ...bulkSelection.filtersSelection,
                      },
                  }
                : {
                      workspaceId: workspaceId ?? "",
                      action,
                      financialDocumentIds: bulkSelection.selected.map(
                          (fd) => fd.id,
                      ),
                  },
        [workspaceId, bulkSelection.filtersSelection, bulkSelection.selected],
    );

    const bulkActionMutation = useFinancialDocumentBulkActionMutation({
        onError: (error) => {
            if (!("code" in error)) {
                return;
            }

            // REMOVING_DOCUMENT_WITH_TRANSACTIONS is handled by the
            // transactions-removal confirmation modal in useDocumentBulkActions.

            if (error.code === BulkActionErrorCode.INVALID_DOCUMENT_CATEGORY) {
                toaster.error(<InvalidDocumentCategoryErrorToast />);
            }

            if (
                error.code ===
                RemovingDocumentErrorCode.REMOVING_DOCUMENT_USED_IN_RECONCILIATION
            ) {
                const names =
                    "documentNames" in error ? error.documentNames : [];
                toaster.error(
                    <ReconciliationErrorToast documentNames={names} />,
                );
                return;
            }

            if (
                error.code ===
                BulkActionErrorCode.DOCUMENT_LINKED_TO_IMPORTED_ACCOUNT
            ) {
                toaster.error(<LinkedToImportedAccountErrorToast />);
            }
        },
    });

    const runBulkAction = useCallback(
        async (action: FinancialDocumentBulkAction) => {
            const payload = buildPayload(action);
            await bulkActionMutation.mutateAsync(payload);
        },
        [buildPayload, bulkActionMutation],
    );

    const value = useMemo(
        () => ({
            run: runBulkAction,
            busy: bulkActionMutation.isPending,
        }),
        [runBulkAction, bulkActionMutation.isPending],
    );

    return (
        <financialDocumentBulkActionsContext.Provider value={value}>
            {children}
        </financialDocumentBulkActionsContext.Provider>
    );
};

const ReconciliationErrorToast: React.FC<{
    documentNames: string[];
}> = ({ documentNames }) => {
    if (documentNames.length === 0) {
        return (
            <>
                Some documents are used in a bank reconciliation and cannot be
                deleted. Undo the reconciliation first.
            </>
        );
    }

    if (documentNames.length === 1) {
        return (
            <>
                <strong>{documentNames[0]}</strong> is used in a bank
                reconciliation and cannot be deleted. Undo the reconciliation
                first.
            </>
        );
    }

    return (
        <>
            The following documents are used in a bank reconciliation and cannot
            be deleted:
            <ul style={{ margin: "8px 0", paddingLeft: 20 }}>
                {documentNames.map((name) => (
                    <li key={name}>
                        <strong>{name}</strong>
                    </li>
                ))}
            </ul>
            Undo the reconciliation first.
        </>
    );
};
